import type {BoardItemsMove} from '../types'
import type {BoardDropPlacement} from './placement'
import type {BoardCardDragData, BoardCellDropData} from './types'

/**
 * What a drop makes of the card that was carried, the cell it landed on and where in that
 * cell it points.
 *
 * No React, so it is exercised with no live `DndContext` around it, the way
 * `packages/workstreams-viewer/components/board/board-dnd.ts` keeps its own resolution
 * pure for the same reason.
 *
 * The payloads arrive already read, by `drag/types.ts`'s own readers, so everything about
 * a foreign or malformed payload is settled before this is called and both of those reach
 * here as `null`.
 */

/**
 * The move a drop describes, or `null` when it describes none.
 *
 * `null` for a drag this package could not read, for a drop on anything that is not one of
 * its cells, and for a drop that leaves the card where it already was.
 *
 * **A drop is a non-move only when nothing about the card's place changes.** A card can
 * change column and keep its row, change row and keep its column, or change both, and each
 * of the three is a real gesture that changes at least one field. Testing the column alone
 * would silently discard every vertical drag on a swimlaned board, and there would be
 * nothing on screen or in a log to say why the board had ignored somebody.
 *
 * **A drop in the cell the card came from is a move on a hand-ordered cell and nothing on
 * any other.** Whether a cell can be hand-ordered is the caller's to state, through
 * `BoardProvider`'s `canReorderCards`, so the cell that answered the drop settles it: an
 * ordered cell publishes a placement, and publishes none for an insertion that would leave
 * the card between the same two neighbours. Everywhere else the only ordering is the
 * board's sort, so a drop that keeps both keys leaves the board exactly as it was, and it
 * is suppressed here rather than handed to every caller to guard against.
 *
 * The placement is matched against the cell that was dropped on before it is read. A cell
 * publishes one while a drag is over it and clears it when the drag leaves, so an unmatched
 * placement belongs to a cell the reader has since dragged out of.
 */
export function resolveBoardDrop(
  drag: BoardCardDragData | null | undefined,
  drop: BoardCellDropData | null | undefined,
  placement?: BoardDropPlacement | null,
): BoardItemsMove | null {
  if (!drag || !drop) return null

  const placed =
    placement && placement.columnKey === drop.columnKey && placement.rowKey === drop.rowKey ? placement : null

  if (!placed && drag.columnKey === drop.columnKey && drag.rowKey === drop.rowKey) return null

  const move: BoardItemsMove = {
    itemIds: [drag.itemId],
    from: {columnKey: drag.columnKey, rowKey: drag.rowKey},
    to: {columnKey: drop.columnKey, rowKey: drop.rowKey},
  }

  // Spread rather than assigned, so a cell with nothing to anchor against reports a move
  // carrying no `anchor` key at all rather than one holding `undefined`.
  return placed?.anchor === undefined ? move : {...move, anchor: placed.anchor}
}
